import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { BAG_TYPES, PhotoUpload, Modal, inp, lbl } from "./GearBits";

const Section = ({ title, open, onToggle, children }) => (
  <div className="border border-zinc-200">
    <button type="button" onClick={onToggle}
      className="w-full flex items-center gap-2 px-3 py-2 text-sm font-bold bg-zinc-50 hover:bg-zinc-100 transition-colors">
      {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} {title}
    </button>
    {open && <div className="p-3 grid sm:grid-cols-2 gap-3">{children}</div>}
  </div>
);

export default function BagForm({ bag, travelers, trips, onSaved, onClose }) {
  const [f, setF] = useState({
    name: "", bag_type: "checked_medium", owner_id: "", owner_name: "", brand: "", model: "",
    color: "", size: "", material: "", weight: "", dimensions: "", luggage_tag: "", serial_number: "",
    lock_type: "", tsa_lock: false, tracker_type: "", tracker_id: "", gps_tracker: false,
    distinguishing_marks: "", description: "", compartments: "", purchase_date: "", purchase_price: "",
    current_value: "", insurance: "", warranty: "", notes: "", photo_url: null, trip_id: "", ...(bag || {}),
  });
  const [sections, setSections] = useState({ id: true, track: false, value: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e });
  const toggle = (k) => setSections((s) => ({ ...s, [k]: !s[k] }));

  const save = async () => {
    if (!f.name.trim()) return toast.error("Give the bag a name or nickname");
    setBusy(true);
    try {
      const owner = travelers.find((t) => t.id === f.owner_id);
      const payload = { ...f, owner_name: f.owner_id === "shared" ? "Shared" : owner?.name || f.owner_name || null,
        owner_id: f.owner_id || null, trip_id: f.trip_id || null };
      if (bag?.id) await api.put(`/gear/bags/${bag.id}`, payload);
      else await api.post("/gear/bags", payload);
      toast.success(bag?.id ? "Bag updated" : "Bag added to your inventory");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save bag");
    } finally { setBusy(false); }
  };

  return (
    <Modal title={bag?.id ? "Edit bag" : "Add a bag / luggage"} onClose={onClose} wide>
      <div className="space-y-4" data-testid="bag-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Name / nickname *</label>
            <input value={f.name} onChange={set("name")} data-testid="bag-name" className={inp} placeholder="Tom's red suitcase" /></div>
          <div><label className={lbl}>Type</label>
            <select value={f.bag_type} onChange={set("bag_type")} data-testid="bag-type" className={inp}>
              {BAG_TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><label className={lbl}>Owner</label>
            <select value={f.owner_id || ""} onChange={set("owner_id")} data-testid="bag-owner" className={inp}>
              <option value="">— No owner set —</option>
              <option value="shared">Shared (family / group)</option>
              {travelers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select></div>
          <div><label className={lbl}>Trip</label>
            <select value={f.trip_id || ""} onChange={set("trip_id")} data-testid="bag-trip" className={inp}>
              <option value="">— Not on a trip —</option>
              {trips.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select></div>
          <div><label className={lbl}>Color</label><input value={f.color || ""} onChange={set("color")} className={inp} placeholder="Red with black trim" /></div>
          <div><label className={lbl}>Brand / model</label>
            <div className="flex gap-2">
              <input value={f.brand || ""} onChange={set("brand")} className={inp} placeholder="Brand" />
              <input value={f.model || ""} onChange={set("model")} className={inp} placeholder="Model" />
            </div></div>
        </div>

        <PhotoUpload value={f.photo_url} onChange={(u) => setF({ ...f, photo_url: u })} testid="bag-photo-upload" />

        <Section title="Identification — spot it at the belt, prove it if lost" open={sections.id} onToggle={() => toggle("id")}>
          <div><label className={lbl}>Distinguishing marks</label><input value={f.distinguishing_marks || ""} onChange={set("distinguishing_marks")} data-testid="bag-marks" className={inp} placeholder="Yellow ribbon on handle, scratch on left side" /></div>
          <div><label className={lbl}>Luggage tag</label><input value={f.luggage_tag || ""} onChange={set("luggage_tag")} className={inp} placeholder="Tag name / number" /></div>
          <div><label className={lbl}>Serial number</label><input value={f.serial_number || ""} onChange={set("serial_number")} className={inp} /></div>
          <div><label className={lbl}>Size / dimensions / weight</label>
            <div className="flex gap-2">
              <input value={f.size || ""} onChange={set("size")} className={inp} placeholder="Size" />
              <input value={f.weight || ""} onChange={set("weight")} className={inp} placeholder="Weight" />
            </div></div>
          <div><label className={lbl}>Material</label><input value={f.material || ""} onChange={set("material")} className={inp} placeholder="Hard shell / fabric" /></div>
          <div><label className={lbl}>Compartments / interior</label><input value={f.description || ""} onChange={set("description")} className={inp} placeholder="2 main + laptop sleeve" /></div>
          <div><label className={lbl}>Lock type</label><input value={f.lock_type || ""} onChange={set("lock_type")} className={inp} placeholder="Combination / key" /></div>
          <label className="flex items-center gap-2 text-sm mt-5"><input type="checkbox" checked={!!f.tsa_lock} onChange={set("tsa_lock")} data-testid="bag-tsa" className="w-4 h-4 accent-primary" /> TSA-compatible lock</label>
        </Section>

        <Section title="Tracking — GPS / Bluetooth / tags" open={sections.track} onToggle={() => toggle("track")}>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.gps_tracker} onChange={set("gps_tracker")} data-testid="bag-gps" className="w-4 h-4 accent-primary" /> GPS tracker installed</label>
          <div><label className={lbl}>Tracker type</label>
            <select value={f.tracker_type || ""} onChange={set("tracker_type")} className={inp}>
              <option value="">None</option><option value="airtag">AirTag / Bluetooth</option>
              <option value="gps">GPS tracker</option><option value="qr">QR / barcode tag</option><option value="rfid">RFID</option>
            </select></div>
          <div><label className={lbl}>Tracker ID / name</label><input value={f.tracker_id || ""} onChange={set("tracker_id")} data-testid="bag-tracker-id" className={inp} /></div>
        </Section>

        <Section title="Value & insurance" open={sections.value} onToggle={() => toggle("value")}>
          <div><label className={lbl}>Purchase date / price</label>
            <div className="flex gap-2">
              <input value={f.purchase_date || ""} onChange={set("purchase_date")} className={inp} placeholder="2024-06" />
              <input value={f.purchase_price || ""} onChange={set("purchase_price")} className={inp} placeholder="$250" />
            </div></div>
          <div><label className={lbl}>Current / replacement value</label><input value={f.current_value || ""} onChange={set("current_value")} className={inp} placeholder="$200" /></div>
          <div><label className={lbl}>Insurance</label><input value={f.insurance || ""} onChange={set("insurance")} className={inp} placeholder="Policy / provider" /></div>
          <div><label className={lbl}>Warranty</label><input value={f.warranty || ""} onChange={set("warranty")} className={inp} /></div>
        </Section>

        <div><label className={lbl}>Notes</label><textarea value={f.notes || ""} onChange={set("notes")} rows={2} className={inp} /></div>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="bag-save" className="px-5 py-2 text-sm font-bold bg-primary text-white hover:bg-blue-700 disabled:opacity-50">
            {busy ? "Saving…" : bag?.id ? "Save changes" : "Add bag"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
